<script setup lang="ts">
/**
 * UI-kit component
 * Category: Modals
 * Name: Confirmation
 */
import type { ModalConfirmationProps } from '@UI/Modals/ModalConfirmation/types/interfaces'

import { useModalConfirmation } from '@UI/Modals/ModalConfirmation/composables/modal-confirmation.hook'
import { ModalDefault } from '@UI/Modals/ModalDefault'
import { AppIcon } from '@UI/Icons/AppIcon'
import { AppText } from '@UI/Typography/AppText'
import { BtnPrimary } from '@UI/Buttons/BtnPrimary'
import { BtnSecondary } from '@UI/Buttons/BtnSecondary'

defineOptions({
  name: 'ModalConfirmation',
})

const props = withDefaults(defineProps<ModalConfirmationProps>(), {
  detail: '',
  show: false,
  loading: false,
  mode: 'confirm',
  buttonTitle: 'Confirm',
  cancelTitle: 'Cancel',
  className: '',
  overlayClassName: '',
  overlayClick: true,
  isCloseIcon: false,
  disabled: false,
})

const emit = defineEmits<{
  'click:confirm': []
  'click:cancel': []
}>()

const {
  refMode,
  refIcon,
  refIconColor,
  refButtonColor,
  handleConfirm,
  handleCancel,
  handleClickOverlay,
} = useModalConfirmation({
  props,
  onConfirm: () => emit('click:confirm'),
  onCancel: () => emit('click:cancel'),
})
</script>

<template>
  <ModalDefault
    :show="show"
    :loading="loading"
    :overlay-class-name="overlayClassName"
    :is-close-button="isCloseIcon"
    @click:overlay="handleClickOverlay"
    @click:close="handleCancel"
  >
    <div :class="['ui-modal-confirm', className]">
      <div class="ui-modal-confirm__box">
        <div class="ui-modal-confirm__title">
          <div :class="['ui-modal-confirm__icon', refMode]">
            <slot name="icon">
              <AppIcon
                v-if="refIcon"
                :icon="refIcon"
                :color="refIconColor"
              />
            </slot>
          </div>
          <AppText
            name="h3"
            tag="div"
            color="black"
          >
            {{ title }}
          </AppText>
        </div>
        <AppText
          v-if="detail"
          name="body1"
          tag="div"
          color="black"
          class="ui-modal-confirm__detail"
        >
          <div v-html="detail" />
        </AppText>
        <div
          v-if="$slots.content"
          class="ui-modal-confirm__content"
        >
          <slot name="content" />
        </div>
        <div
          v-if="$slots.other"
          class="ui-modal-confirm__other"
        >
          <slot name="other" />
        </div>
        <div class="ui-modal-confirm__buttons">
          <BtnPrimary
            v-if="buttonTitle"
            :text="buttonTitle"
            :color="refButtonColor"
            :disabled="loading || disabled"
            @click="handleConfirm"
          />
          <BtnSecondary
            v-if="cancelTitle"
            :text="cancelTitle"
            :disabled="loading"
            @click="handleCancel"
          />
        </div>
      </div>
    </div>
  </ModalDefault>
</template>

<style lang="scss" scoped src="../styles/_modal-confirmation.scss" />
